import Alert from "@mui/material/Alert";
import type { AutocompleteInputChangeReason } from "@mui/material/Autocomplete";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Switch from "@mui/material/Switch";
import { observable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "@/common/axios";
import type { DEFAULT_STATE } from "@/Export/constants";
import DMPTableSmall from "../eln-dmp-integration/DMPTool/DMPTableSmall";
import { mapNullable } from "../util/Util";
import type { Validator } from "../util/Validator";
import type { DMPUserInternalId, Person, Repo, RepoDetails } from "./repositories/common";
import DataverseRepo from "./repositories/DataverseRepo";
import DigitalCommonsDataRepo from "./repositories/DigitalCommonsDataRepo";
import DryadRepo from "./repositories/DryadRepo";
import FigshareRepo from "./repositories/FigshareRepo";
import type { Tag } from "./repositories/Tags";
import ZenodoRepo from "./repositories/ZenodoRepo";

const STANDARD_VALIDATIONS = {
  description: false,
  title: false,
  author: false,
  contact: false,
  subject: false,
};

const DRYAD_VALIDATIONS = {
  ...STANDARD_VALIDATIONS,
  crossrefFunder: false,
};

type ExportRepoArgs = {
  state: typeof DEFAULT_STATE;
  validator: Validator;
  repoList: Array<Repo>;
  repoDetails: RepoDetails;
  updateRepoConfig: (repoDetails: RepoDetails) => void;
  fetchTags: () => Promise<Array<Tag>>;
};

const DmpSelector = observer(
  ({
    state,
    handleSwitch,
    repo,
  }: {
    state: { selectedPlans: Array<DMPUserInternalId>; linkDMP: boolean };
    handleSwitch: (foo: "linkDMP") => (event: { target: { checked: boolean } }) => void;
    repo: Repo;
  }) => {
    const { t } = useTranslation("workspace");
    const addSelectedPlan = (id: DMPUserInternalId) => {
      if (!state.selectedPlans.includes(id)) {
        runInAction(() => {
          state.selectedPlans = [...state.selectedPlans, id];
        });
      }
    };

    const removeSelectedPlan = (id: DMPUserInternalId) => {
      runInAction(() => {
        state.selectedPlans = state.selectedPlans.filter((planId) => planId !== id);
      });
    };

    return mapNullable(
      (dmps) => (
        <Grid container sx={{ width: "100%" }}>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={state.linkDMP}
                  onChange={handleSwitch("linkDMP")}
                  value="link DMP"
                  color="primary"
                  data-test-id="link DMP"
                  slotProps={{ input: { role: "checkbox" } }}
                />
              }
              label={t("export.repositories.dmp.associateLabel")}
            />
          </Grid>
          <Grid size={12}>
            <Collapse in={state.linkDMP} component="div" collapsedSize={0}>
              <DMPTableSmall
                plans={dmps}
                selectedPlans={state.selectedPlans}
                addSelectedPlan={addSelectedPlan}
                removeSelectedPlan={removeSelectedPlan}
              />
              {repo.repoName === "app.zenodo" && state.selectedPlans.length > 1 && (
                <Alert severity="error">{t("export.repositories.dmp.zenodoLimit")}</Alert>
              )}
            </Collapse>
          </Grid>
        </Grid>
      ),
      repo.linkedDMPs,
    );
  },
);

function ExportRepo({
  state: upstreamState,
  validator,
  repoList,
  repoDetails,
  updateRepoConfig,
  fetchTags,
}: ExportRepoArgs): React.ReactNode {
  const { t } = useTranslation("workspace");
  const [state] = useState(
    observable({
      inputValidations: STANDARD_VALIDATIONS,
      submitAttempt: false,
      repoChoice: repoDetails.repoChoice,
      title: repoDetails.meta.title,
      description: repoDetails.meta.description,
      subject: repoDetails.meta.subject,
      license: 0,
      publish: repoDetails.meta.publish,
      authors: repoDetails.meta.authors,
      contacts: repoDetails.meta.contacts,
      otherProperties: {},
      linkDMP: false,
      selectedPlans: [] as Array<DMPUserInternalId>,
      crossrefFunders: [] as Array<{ name: string }>,
      selectedFunder: {},
      tags: [] as Array<Tag>,
      metadataLanguage: "",
      exportToRaid: upstreamState.repositoryConfig.exportToRaid,
    }),
  );
  const [fetchingTags, setFetchingTags] = useState(false);

  useEffect(() => {
    void (async () => {
      setFetchingTags(true);
      const tags = await fetchTags();
      runInAction(() => {
        state.tags = tags;
      });
      setFetchingTags(false);
    })();
  }, []);

  useEffect(() => {
    validator.setValidFunc(() => {
      const repo = repoList[state.repoChoice];
      let validations: Record<string, boolean> = { ...STANDARD_VALIDATIONS };

      if (repo.repoName === "app.dryad") {
        validations = { ...DRYAD_VALIDATIONS };
        if (
          "funder" in state.otherProperties &&
          typeof state.otherProperties.funder === "string" &&
          state.otherProperties.funder.length !== 0
        )
          validations.crossrefFunder = true;
      }

      if (state.description.length > 2) validations.description = true;
      if (state.subject !== "") validations.subject = true;
      if (state.title.length > 2) validations.title = true;
      if (state.authors.length) validations.author = true;
      if (state.contacts.length) validations.contact = true;

      runInAction(() => {
        state.inputValidations = validations as typeof STANDARD_VALIDATIONS | typeof DRYAD_VALIDATIONS;
        state.submitAttempt = true;
      });

      return Promise.resolve(
        [...Object.values(validations), !(repo.repoName === "app.zenodo" && state.selectedPlans.length > 1)].every(
          (b) => b,
        ),
      );
    });
  }, []);

  const getParams = () => {
    const repo = repoList[state.repoChoice];

    return {
      repoChoice: state.repoChoice,
      appName: repo.repoName,
      depositToRepository: true,
      repoCfg: repo.repoCfg,
      selectedDMPs: state.selectedPlans,
      meta: {
        title: state.title,
        description: state.description,
        subject: state.subject,
        licenseName: repo.license.licenses[state.license].licenseDefinition.name,
        licenseUrl: repo.license.licenses[state.license].licenseDefinition.url,
        authors: state.authors,
        contacts: state.contacts,
        publish: state.publish,
        otherProperties: state.otherProperties,
        tags: state.tags,
      },
      exportToRaid: state.exportToRaid,
    };
  };

  const updateRemoteConfig = () => {
    const params = getParams();
    updateRepoConfig(params);
  };

  const handleChange = <Key extends keyof typeof state>(event: {
    target: { name: Key; value: (typeof state)[Key] };
  }) => {
    runInAction(() => {
      state[event.target.name] = event.target.value;
    });
    updateRemoteConfig();
  };

  const handleMetadataLanguageChange = (event: { target: { name: string; value: string } }) => {
    runInAction(() => {
      state.otherProperties = { metadataLanguage: event.target.value };
      state.metadataLanguage = event.target.value;
    });
    updateRemoteConfig();
  };

  const handleSwitch =
    <Key extends keyof typeof state>(name: Key): ((event: { target: { checked: boolean } }) => void) =>
    (event) => {
      runInAction(() => {
        // @ts-expect-error the type of state[name] might not be a boolean
        state[name] = event.target.checked;
      });
    };

  const updatePeople = (people: Array<Person>) => {
    runInAction(() => {
      state.authors = people.filter((person) => person.type === "Author");
      state.contacts = people.filter((person) => person.type === "Contact");
    });
    updateRemoteConfig();
  };

  const handleCrossrefFunderChange = (_: unknown, values: { name: string }) => {
    runInAction(() => {
      state.otherProperties = {
        funder: JSON.stringify(values),
      };
    });
    updateRemoteConfig();
  };

  const fetchCrossrefFunders = async (query: string) => {
    try {
      const searchParams = new URLSearchParams();
      if (query) {
        searchParams.append("query", query);
      }
      const { data } = await axios.get<{
        message: { items: Array<{ name: string }> };
      }>("https://api.crossref.org/funders", {
        params: searchParams,
      });
      runInAction(() => {
        state.crossrefFunders = data.message.items;
      });
      updateRemoteConfig();
    } catch (error) {
      console.error("Error retrieving funders from crossref API:", error);
    }
  };

  const handleFetchCrossrefFunder = (
    _event: React.SyntheticEvent<Element, Event>,
    searchTerm: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (searchTerm && searchTerm.length > 2 && reason === "input") {
      void fetchCrossrefFunders(searchTerm);
    }
  };

  const repo: Repo = repoList[state.repoChoice];

  return (
    <Grid container sx={{ flexDirection: "column", width: "100%" }} spacing={1}>
      <Grid size={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t("export.repositories.choiceLabel")}</FormLabel>
          <RadioGroup
            aria-label={t("export.repositories.labels.choice")}
            name="repoChoice"
            value={state.repoChoice.toString()}
            // @ts-expect-error React event handlers are not parameterised by the name prop
            onChange={handleChange}
          >
            {repoList.map((r, i) => (
              <div key={r.repoName}>
                <FormControlLabel
                  value={`${i}`}
                  control={
                    <Radio
                      color="primary"
                      slotProps={{
                        input: {
                          "data-testid": `radio-button-${r.repoName}`,
                        } as Record<string, unknown>,
                      }}
                    />
                  }
                  label={r.displayName + (r.label ? ` - ${r.label}` : "")}
                  data-testid={`repo-label-${r.repoName}`}
                />
              </div>
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid>
        <Divider orientation="horizontal" />
      </Grid>
      <Grid>
        {repo.repoName === "app.dryad" && (
          <>
            <DmpSelector state={state} handleSwitch={handleSwitch} repo={repo} />
            <DryadRepo
              repo={repo}
              handleChange={handleChange}
              updatePeople={updatePeople}
              handleCrossrefFunderChange={handleCrossrefFunderChange}
              handleFetchCrossrefFunder={handleFetchCrossrefFunder}
              crossrefFunders={state.crossrefFunders}
              inputValidations={state.inputValidations as typeof DRYAD_VALIDATIONS}
              submitAttempt={state.submitAttempt}
              contacts={state.contacts}
              authors={state.authors}
            />
          </>
        )}
        {repo.repoName === "app.dataverse" && (
          <>
            <DmpSelector state={state} handleSwitch={handleSwitch} repo={repo} />
            <DataverseRepo
              repo={repo}
              handleChange={handleChange}
              updatePeople={updatePeople}
              inputValidations={state.inputValidations}
              submitAttempt={state.submitAttempt}
              contacts={state.contacts}
              authors={state.authors}
              title={state.title}
              description={state.description}
              license={state.license}
              subject={state.subject}
              onTagsChange={({ target: { value } }) => {
                runInAction(() => {
                  state.tags = value;
                });
                updateRemoteConfig();
              }}
              tags={state.tags}
              fetchingTags={fetchingTags}
              metadataLanguage={state.metadataLanguage}
              handleMetadataLanguageChange={handleMetadataLanguageChange}
            />
          </>
        )}
        {repo.repoName === "app.figshare" && (
          <>
            <DmpSelector state={state} handleSwitch={handleSwitch} repo={repo} />
            <FigshareRepo
              repo={repo}
              handleChange={handleChange}
              updatePeople={updatePeople}
              inputValidations={state.inputValidations}
              submitAttempt={state.submitAttempt}
              contacts={state.contacts}
              authors={state.authors}
              title={state.title}
              description={state.description}
              license={state.license}
              publish={state.publish}
              subject={state.subject}
            />
          </>
        )}
        {repo.repoName === "app.zenodo" && (
          <>
            <DmpSelector state={state} handleSwitch={handleSwitch} repo={repo} />
            <ZenodoRepo
              handleChange={handleChange}
              updatePeople={updatePeople}
              inputValidations={state.inputValidations}
              submitAttempt={state.submitAttempt}
              title={state.title}
              description={state.description}
              tags={state.tags}
              fetchingTags={fetchingTags}
              onTagsChange={({ target: { value } }) => {
                runInAction(() => {
                  state.tags = value;
                });
                updateRemoteConfig();
              }}
            />
          </>
        )}
        {repo.repoName === "app.digitalCommonsData" && (
          <DigitalCommonsDataRepo
            handleChange={handleChange}
            updatePeople={updatePeople}
            inputValidations={state.inputValidations}
            submitAttempt={state.submitAttempt}
            title={state.title}
            description={state.description}
            tags={state.tags}
            fetchingTags={fetchingTags}
            onTagsChange={() => {}}
          />
        )}
      </Grid>
    </Grid>
  );
}

export default observer(ExportRepo);
